import { api } from "@/api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { checkCustomerExists } from "../api/auth";
import { routes } from "../config";
import type { RootState } from "../store";
import { useAuth } from "@/auth/AuthContext";
import { useLocalization } from "@/contexts/LocalizationContext";
import { useAppDispatch } from "@/store/hooks";
import { setStoredPhoneNumber, setVerificationId } from "@/store/authSlice";
import { FirebaseUser } from "@/types";
import { AuthShell } from "@/components/AuthShell";

import {
  clearRecaptchaVerifier,
  readPhoneSession,
  sendOtpSms,
  storePhoneSession,
  verifyOtpWithVerificationId,
} from "../lib/firebase";

export default function OTP() {
  const { t } = useLocalization();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { updateCustomer } = useAuth();

  const reduxPhoneNumber = useSelector(
    (state: RootState) => (state as any).auth.phoneNumber
  );
  const reduxVerificationId = useSelector(
    (state: RootState) => (state as any).auth.verificationId
  );

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationId, setVerificationIdState] = useState<string>("");

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasNavigated = useRef(false);

  const [resendTimer, setResendTimer] = useState(60);

  const RESEND_RECAPTCHA_BUTTON_ID = "resend-otp-button";

  // Init required session data once (survives refresh via sessionStorage)
  useEffect(() => {
    const session = readPhoneSession();

    const pn = reduxPhoneNumber || session.phoneNumber;
    const vid = reduxVerificationId || session.verificationId;

    if (!pn || !vid) {
      navigate(routes.login, { replace: true });
      return;
    }

    setPhoneNumber(pn);
    setVerificationIdState(vid);

    // Ensure Redux has it too
    dispatch(setStoredPhoneNumber(pn));
    dispatch(setVerificationId(vid));
  }, [dispatch, navigate, reduxPhoneNumber, reduxVerificationId]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  // Cleanup verifier for resend button when leaving page
  useEffect(() => {
    return () => {
      clearRecaptchaVerifier(RESEND_RECAPTCHA_BUTTON_ID);
    };
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    // Handle autocomplete/SMS suggestion where full OTP is pasted into first field
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "");
      if (digits.length > 0) {
        const newOtp = [...otp];
        for (let i = 0; i < 6 && i < digits.length; i++) {
          newOtp[i] = digits[i];
        }
        setOtp(newOtp);
        // Focus appropriate input
        const focusIndex = Math.min(digits.length, 5);
        inputRefs.current[focusIndex]?.focus();
      }
      return;
    }
    
    if (value && !/^[0-9]$/.test(value)) return;

    const next = [...otp];
    next[index] = value;
    setOtp(next);

    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "");
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6 - index && i < digits.length; i++) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);
      // Focus appropriate input
      const focusIndex = Math.min(index + digits.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const code = otp.join("");
    if (code.length !== 6) {
      setError(t("auth.errorMessages.otp.lengthInvalid"));
      return;
    }
    if (isLoading || hasNavigated.current) return;

    setIsLoading(true);
    setError("");

    try {
      if (!verificationId) {
        setError(t("auth.errorMessages.otp.noSessionFound"));
        return;
      }

      const result = await verifyOtpWithVerificationId(verificationId, code);

      api.defaults.headers.Authorization = `Bearer ${result.token}`;

      // Your existing flow:
      try {
        const customerCheck = await checkCustomerExists();

        if (customerCheck.exists && customerCheck.customer) {
          const firebaseUser: FirebaseUser = {
            uid: result.user.uid,
            email: result.user.email || null,
            phoneNumber: result.user.phoneNumber || null,
          };

          updateCustomer(customerCheck.customer, firebaseUser);

          if (!hasNavigated.current) {
            hasNavigated.current = true;
            navigate(routes.home, { replace: true });
          }
        } else {
          if (!hasNavigated.current) {
            hasNavigated.current = true;
            navigate(routes.register, { replace: true });
          }
        }
      } catch (customerError) {
        console.error("OTP: Error checking customer:", customerError);
        if (!hasNavigated.current) {
          hasNavigated.current = true;
          navigate(routes.register, { replace: true });
        }
      }
    } catch (err: any) {
      console.error("OTP verification error:", err);

      let errorMessage = t("auth.errorMessages.otp.verificationFailed");
      if (err?.code === "auth/invalid-verification-code") {
        errorMessage = t("auth.errorMessages.otp.verificationFailed");
      } else if (err?.code === "auth/code-expired") {
        errorMessage = t("auth.errorMessages.otp.codeExpired");
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTP = async () => {
    if (resendTimer > 0 || isLoading) return;
    if (!phoneNumber) return;

    setIsLoading(true);
    setError("");

    try {
      setResendTimer(60);

      const confirmation = await sendOtpSms({
        phoneNumberE164: phoneNumber,
        recaptchaButtonId: RESEND_RECAPTCHA_BUTTON_ID,
      });

      // Update session + redux for the new verificationId
      storePhoneSession(phoneNumber, confirmation.verificationId);
      dispatch(setVerificationId(confirmation.verificationId));
      setVerificationIdState(confirmation.verificationId);
    } catch (err: any) {
      console.error("Resend OTP error:", err);

      let errorMessage = t("auth.errorMessages.otp.resendFailed");
      if (err?.code === "auth/too-many-requests") {
        errorMessage = t("auth.errorMessages.otp.tooManyRequests");
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <Box component="form" onSubmit={handleSubmit}>
              <Typography
                sx={{
                  textAlign: "center",
                  mb: 4,
                  color: "#3D3D3D",
                  fontSize: "16px",
                  fontWeight: 400,
                  fontFamily: "Montserrat",
                  lineHeight: "22px",
                  letterSpacing: "0.28px",
                }}
              >
                {t("auth.otp.enterCodeSentTo")}{" "}
                <span style={{ fontWeight: 600, fontSize: "18px" }}>
                  {phoneNumber}
                </span>
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mb: 4,
                }}
              >
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={el => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onPaste={e => handlePaste(e, index)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    type="tel"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 6,
                      autoComplete: index === 0 ? "one-time-code" : "off",
                      style: {
                        textAlign: "center",
                        fontSize: "18px",
                        fontWeight: 600,
                      },
                    }}
                    sx={{
                      width: '50px',
                      color: '#000000',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#F5F5F5',
                        border: '1px solid #5B5B5B',
                        color: '#000000',
                        height: '50px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiInputBase-input": {
                          textAlign: "center",
                          padding: "12px 8px",
                        },
                      },
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="body2" sx={{ color: "#3d3d3d", mb: 0.5 }}>
                  {t("auth.otp.notReceived")}
                </Typography>

                {/* Use Button-like behavior; Link "disabled" is not reliable */}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => {
                    if (resendTimer > 0 || isLoading) return;
                    resendOTP();
                  }}
                  sx={{
                    color: resendTimer > 0 || isLoading ? "#999" : theme.palette.warning.main,
                    textDecoration: "underline",
                    cursor:
                      resendTimer > 0 || isLoading ? "not-allowed" : "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  aria-disabled={resendTimer > 0 || isLoading}
                >
                  {t("auth.otp.resend")}{" "}
                  {resendTimer > 0 ? `(${resendTimer}s)` : ""}
                </MuiLink>

                {/* Hidden button that reCAPTCHA binds to for RESEND */}
                <button
                  id={RESEND_RECAPTCHA_BUTTON_ID}
                  type="button"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    width: 1,
                    height: 1,
                    opacity: 0,
                  }}
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading || otp.join("").length !== 6}
                  sx={{
                    width: '100%',
                    height: '53px',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#999',
                    },
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ color: "white", mr: 1 }}
                      />
                      {t("auth.messages.verifying")}
                    </>
                  ) : (
                    t("common.continue")
                  )}
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
    </AuthShell>
  );
}
